import { DataService } from "@/services/data";
import { FieldDataType } from "@/types/dynamic-detail";
import { hasStaticOptions } from "@/utils/static-options";
import { flattenOptionFields } from "@/utils/field-options";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type DynamicOption = {
    value: string | null;
    label: string;
    metadata?: Record<string, unknown> | null;
    /** Listed but not selectable (`options_handler` of `form-options`), with the reason. */
    disabled?: boolean;
    description?: string;
};
type Option = DynamicOption;
type OptionMap = Record<string, Option[]>;

/**
 * Header that makes the server skip its response cache (`CacheMixin`): the picklists are cached
 * up to a few minutes, so a value created meanwhile (a session opened a minute ago) would not
 * show until then. Sent only by an explicit refresh.
 */
export const NOCACHE_HEADERS = { "Walter-Cache-Control": "nocache" } as const;

/** The options a picklist endpoint answers with: a bare list or a paginated envelope. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function optionsFromResponse(data: any): Option[] {
    const results = Array.isArray(data) ? data : data?.results || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return results.map((item: any) => ({
        value: item.value?.toString() ?? item.id?.toString() ?? (typeof item === "string" ? item : null),
        label: item.label ?? item.name ?? String(item),
        // Attributes the form's conditions read (`option_attribute`), served by `form-options`.
        ...(item?.metadata ? { metadata: item.metadata } : {}),
        // Listed but not selectable, with the reason (a full session, say).
        ...(item?.disabled ? { disabled: true } : {}),
        ...(typeof item?.description === "string" && item.description ? { description: item.description } : {})
    }));
}

/**
 * What remains of a selection once its list was reloaded: the values still offered.
 *
 * A single value no longer offered becomes `null`; a multiple selection (an array, or the
 * comma-joined string the combobox writes) keeps the values still offered. A value listed but
 * `disabled` (a closed session) is not offered. The same value is returned when nothing has to
 * change, so the caller can skip a write.
 */
export function keepAvailableSelection(current: unknown, options: { value: string | null; disabled?: boolean }[], multiple?: boolean): unknown {
    if (current == null || current === "") return current;
    const offered = new Set(options.filter(option => !option.disabled).map(option => (option.value === null ? "__null__" : String(option.value))));
    if (Array.isArray(current)) {
        const kept = current.filter(value => offered.has(String(value)));
        return kept.length === current.length ? current : kept;
    }
    if (multiple && typeof current === "string") {
        // Read item by item as `splitMultipleValues` does: « 1, 2 » holds « 1 » and « 2 ».
        const values = current
            .split(",")
            .map(value => value.trim())
            .filter(Boolean);
        const kept = values.filter(value => offered.has(value));
        return kept.length === values.length ? current : kept.join(",");
    }
    return offered.has(String(current)) ? current : null;
}

/**
 * Options the fields declaring `document_options` take, keyed by descriptor path.
 *
 * Exported so it can be tested on its own, and kept a pure function for a reason: unlike
 * a referential, a document list has nothing to load and nothing to wait for. It arrived
 * with the payload the form is already rendering, so resolving it through state would
 * only give the author one render of an empty selector — and, on the canvas editor where
 * the graph changes on every keystroke, a stale list.
 */
export function documentOptionsByField(fields: FieldDataType[], provided: OptionMap | undefined): OptionMap {
    if (!provided) return {};
    const resolved: OptionMap = {};
    flattenOptionFields(fields).forEach(({ field, key }) => {
        if (!field.document_options) return;
        resolved[key] = provided[field.document_options] ?? [];
    });
    return resolved;
}

/**
 * `options_url` with the field's `options_params` (dependent options).
 *
 * A `$name` value is read from the values being edited, so the list follows the choice made
 * above it (the sessions of the product picked). Any other value arrived resolved from the
 * record. An empty value is left out: the server then narrows nothing on that parameter.
 */
export function resolveOptionsUrl(field: FieldDataType, values?: Record<string, unknown>): string | undefined {
    const url = field.options_url;
    if (!url || !field.options_params) return url;
    const query = new URLSearchParams();
    Object.entries(field.options_params).forEach(([key, raw]) => {
        const value = raw.startsWith("$") ? values?.[raw.slice(1)] : raw;
        const text = Array.isArray(value) ? value.filter(item => item != null && item !== "").join(",") : value == null ? "" : String(value);
        if (text !== "") query.set(key, text);
    });
    const search = query.toString();
    if (!search) return url;
    return `${url}${url.includes("?") ? "&" : "?"}${search}`;
}

/** Whether a field list reads the values being edited to build its option URLs (`$name` params). */
export function readsFormValues(fields: FieldDataType[]): boolean {
    return flattenOptionFields(fields).some(({ field }) => Object.values(field.options_params ?? {}).some(value => value.startsWith("$")));
}

/**
 * Hook to load dynamic options for select/combobox fields.
 * Handles static options, `options_url` loading and the lists the document carries, at
 * any depth.
 *
 * `documentOptions` is what a field with no reference list to fetch reads instead: a
 * counter naming one of the steps of the graph being edited has no process-wide URL that
 * could answer it, so its options ride along with the document — see the `options` key of
 * the graph projection.
 *
 * `values` are the values being edited, for the fields whose options depend on them
 * (`options_params`): a list is fetched again when the URL it resolves to changes.
 */
export function useDynamicOptions(fields: FieldDataType[], documentOptions?: OptionMap, values?: Record<string, unknown>) {
    const [dynamicOptions, setDynamicOptions] = useState<OptionMap>({});
    const [loadingOptions, setLoadingOptions] = useState<Record<string, boolean>>({});
    // Descriptor paths whose last load failed: their list is not known. `dynamicOptions` only ever holds a
    // list that was actually loaded (or the static one), never a failure stored as an empty list, so a
    // reader can tell « nothing offered » from « not known ».
    const [failedOptions, setFailedOptions] = useState<Record<string, boolean>>({});
    // URL last requested per descriptor path, so an unchanged URL is not fetched again.
    const requested = useRef<Record<string, string>>({});
    // Id of the latest request per descriptor path (automatic load or refresh): only its answer,
    // success or failure, is applied. A URL alone cannot tell a cached GET still in flight from the
    // refresh of the same URL that overtook it.
    const sequence = useRef<Record<string, number>>({});
    // URL the list on screen was loaded from: a failure keeps a list already shown for that URL.
    const loadedFrom = useRef<Record<string, string>>({});
    const nextRequest = (key: string) => {
        sequence.current[key] = (sequence.current[key] ?? 0) + 1;
        return sequence.current[key];
    };
    const isLatest = (key: string, id: number) => sequence.current[key] === id;
    const urlsKey = JSON.stringify(flattenOptionFields(fields).map(({ field, key }) => [key, resolveOptionsUrl(field, values)]));

    useEffect(() => {
        flattenOptionFields(fields).forEach(({ field, key }) => {
            // Options the document carries are resolved below, without a request.
            if (field.document_options) return;
            if (field.field_type !== "select" && field.field_type !== "combobox") return;
            const url = hasStaticOptions(field) ? undefined : resolveOptionsUrl(field, values);
            // Dynamic options via options_url
            if (url) {
                if (requested.current[key] === url) return;
                requested.current[key] = url;
                const id = nextRequest(key);
                setLoadingOptions(prev => ({ ...prev, [key]: true }));
                DataService.client
                    .url(url)
                    .get()
                    .json<any>()
                    .then(data => {
                        if (!isLatest(key, id)) return;
                        loadedFrom.current[key] = url;
                        setDynamicOptions(prev => ({ ...prev, [key]: optionsFromResponse(data) }));
                        setFailedOptions(prev => (prev[key] ? { ...prev, [key]: false } : prev));
                    })
                    .catch(() => {
                        if (!isLatest(key, id)) return;
                        // A list already shown for this URL stays; one for other parameters is wrong now and is
                        // dropped (not known: nothing is taken as « no longer offered »). The URL may be asked again.
                        if (loadedFrom.current[key] !== url) {
                            setDynamicOptions(prev => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                            });
                            if (requested.current[key] === url) delete requested.current[key];
                        }
                        setFailedOptions(prev => ({ ...prev, [key]: true }));
                    })
                    .finally(() => {
                        if (isLatest(key, id)) setLoadingOptions(prev => ({ ...prev, [key]: false }));
                    });
            }
            // Static options
            else if (field.options && !dynamicOptions[key]) {
                setDynamicOptions(prev => ({
                    ...prev,
                    [key]: field.options!.map(opt => ({ value: opt.value, label: opt.label }))
                }));
            }
        });
        // `values` is read through `urlsKey`: a keystroke elsewhere in the form fetches nothing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, urlsKey]);

    const merged = useMemo(() => ({ ...dynamicOptions, ...documentOptionsByField(fields, documentOptions) }), [fields, dynamicOptions, documentOptions]);

    // Read by `reloadOptions`, which keeps one identity for the whole life of the form.
    const latest = useRef({ fields, values });
    latest.current = { fields, values };
    const [reloadingOptions, setReloadingOptions] = useState<Record<string, boolean>>({});

    /**
     * Fetch the list of `key` (a descriptor path) again, bypassing the server cache.
     *
     * The current list stays on screen until the new one arrives, and stays if the request fails,
     * so a refresh never empties a selector. Resolves to the new options, or `undefined` when
     * nothing was reloaded (no URL, a failure, or a newer request overtook this one).
     */
    const reloadOptions = useCallback(async (key: string): Promise<Option[] | undefined> => {
        const entry = flattenOptionFields(latest.current.fields).find(candidate => candidate.key === key);
        if (!entry || entry.field.document_options) return undefined;
        const url = resolveOptionsUrl(entry.field, latest.current.values);
        if (!url) return undefined;
        requested.current[key] = url;
        const id = nextRequest(key);
        setReloadingOptions(prev => ({ ...prev, [key]: true }));
        try {
            const data = await DataService.client.url(url).headers(NOCACHE_HEADERS).get().json<unknown>();
            if (!isLatest(key, id)) return undefined;
            const options = optionsFromResponse(data);
            loadedFrom.current[key] = url;
            setDynamicOptions(prev => ({ ...prev, [key]: options }));
            setFailedOptions(prev => (prev[key] ? { ...prev, [key]: false } : prev));
            return options;
        } catch {
            // The list on screen stays: a refresh never empties a selector.
            return undefined;
        } finally {
            setReloadingOptions(prev => ({ ...prev, [key]: false }));
            // This request overtook an automatic load still in flight: that load is ignored now.
            if (isLatest(key, id)) setLoadingOptions(prev => ({ ...prev, [key]: false }));
        }
        // `nextRequest` / `isLatest` only read refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { dynamicOptions: merged, loadingOptions, failedOptions, reloadOptions, reloadingOptions };
}
